import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FiSearch } from 'react-icons/fi'
import {
  SearchBarWrapper,
  SearchIconWrapper,
  StyledSearchInput,
  ClearButton,
  ShortcutHint,
} from './styles'

interface SearchBarProps {
  placeholder?: string
  onSearch: (value: string) => void
  initialValue?: string
  debounceMs?: number
}

export const SearchBar = ({
  placeholder,
  onSearch,
  initialValue = '',
  debounceMs = 300,
}: SearchBarProps) => {
  const { t } = useTranslation()
  const resolvedPlaceholder = placeholder ?? t('common.searchBar.defaultPlaceholder')
  const [value, setValue] = useState(initialValue)
  const inputRef = useRef<HTMLInputElement>(null)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Keep value in sync if initialValue changes externally (e.g. URL param)
  useEffect(() => {
    setValue(initialValue)
  }, [initialValue])

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const next = e.target.value
      setValue(next)
      if (timerRef.current) clearTimeout(timerRef.current)
      timerRef.current = setTimeout(() => {
        onSearch(next)
      }, debounceMs)
    },
    [onSearch, debounceMs]
  )

  const handleClear = useCallback(() => {
    setValue('')
    onSearch('')
    inputRef.current?.focus()
  }, [onSearch])

  // Cmd+K / Ctrl+K shortcut to focus the search input
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        inputRef.current?.focus()
        inputRef.current?.select()
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])

  // Clean up debounce timer on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [])

  return (
    <SearchBarWrapper>
      <SearchIconWrapper>
        <FiSearch size={16} />
      </SearchIconWrapper>
      <StyledSearchInput
        ref={inputRef}
        type="text"
        value={value}
        onChange={handleChange}
        placeholder={resolvedPlaceholder}
      />
      {value ? (
        <ClearButton onClick={handleClear} aria-label={t('common.searchBar.clearAriaLabel')} type="button">
          ×
        </ClearButton>
      ) : (
        <ShortcutHint>⌘K</ShortcutHint>
      )}
    </SearchBarWrapper>
  )
}
